// Carrier-response status that partner integrations (PBG) set on a benefit's
// escalation.
//
// No value (null / undefined) means nothing for an admin to look at; no member for it.
// Maps to "no dot" on App Engine v2.
//
// Resets to null when a later submission is sent to the carrier or an admin clears the
// escalation. The cause of a FAILED_PRE_CARRIER (e.g. binder card declined) is not in
// this field: it is on the PBG sync task's TASK_CANCELLED event, shown in the
// application's Partner Integration panel.
//
// Coupling: values must match PBG's and EPS's CARRIER_RESPONSE_STATUS byte-for-byte —
// a drifted value silently renders no dot, and EPS drops it from the list filter.
export const CARRIER_RESPONSE_STATUS = {
  /** Carrier confirmed the enrollment is effectuated. Never set by a submit alone. Green dot. */
  SUCCESS: 'success',

  /**
   * Carrier rejected the application, or the submit failed terminally. An outcome where
   * arrival is unknown (timeout, 5xx) leaves the field null instead. Red dot.
   */
  FAILED: 'failed',

  /**
   * Carrier never received the application. Nothing retries it: an admin fixes the
   * cause and resubmits. Orange dot.
   */
  FAILED_PRE_CARRIER: 'failed_pre_carrier',
} as const

export type CarrierResponseStatus =
  (typeof CARRIER_RESPONSE_STATUS)[keyof typeof CARRIER_RESPONSE_STATUS]

// admin-web-client UI only from here down — not part of the cross-repo wire contract.

export const CARRIER_RESPONSE_DOT_COLOR = {
  [CARRIER_RESPONSE_STATUS.SUCCESS]: '#52c41a',
  [CARRIER_RESPONSE_STATUS.FAILED]: '#ff4d4f',
  // antd orange-7, not orange-6 (#fa8c16): 3.6:1 against white vs 2.4:1, clearing
  // the 3:1 WCAG minimum for a graphic that carries meaning.
  [CARRIER_RESPONSE_STATUS.FAILED_PRE_CARRIER]: '#d46b08',
} as const satisfies Record<CarrierResponseStatus, string>

export const CARRIER_RESPONSE_LABEL = {
  [CARRIER_RESPONSE_STATUS.SUCCESS]: 'Carrier Confirmed',
  [CARRIER_RESPONSE_STATUS.FAILED]: 'Carrier Response Failure',
  [CARRIER_RESPONSE_STATUS.FAILED_PRE_CARRIER]: 'Never Reached Carrier',
} as const satisfies Record<CarrierResponseStatus, string>

export const ESCALATION_CARRIER_RESPONSES: readonly CarrierResponseStatus[] = [
  CARRIER_RESPONSE_STATUS.FAILED,
  CARRIER_RESPONSE_STATUS.FAILED_PRE_CARRIER,
]
